import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import App from './App'
import './index.css'
import { initializeWebBrand } from './config/brand'
import { setAppDebugMode } from './config/debugMode'
import { RestaurantService, initializeLayoutSystem, getSupabaseClient, setRestaurantId, useRestaurantStore, geolocationService, findClosestLocation, filterHiddenFromMenu, createAuthCallbacks, shouldRetryQuery } from '@restaurant/core'
import type { UserLocation, RawMenuResponse } from '@restaurant/core'
import { resolveTenant } from './lib/tenant'
import { LoadingScreen } from './components/LoadingScreen'
import { ApiClient, apiClient as singletonApiClient, configureBaseUrl } from '@restaurant/api-client'
import { transformMenuResponse, type RawMenuDataItem } from './lib/menuTransform'

// Configure API base URL from Vite env vars (must happen before any API calls)
configureBaseUrl(import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_FLASK_BASE_URL || 'http://localhost:8000')
import { createWebLogger } from './logger'
import { safeGetLogger } from '@restaurant/logging'
import { isShowcase, getShowcaseToken, getShowcasePayload } from './showcase/showcase'
import { bootShowcase } from './showcase/boot'
import { fetchTokenPayload } from './showcase/tokenFeed'

/**
 * Decode a JWT token to get its payload (without verifying signature).
 * Returns null if the token is malformed.
 */
function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3 || !parts[1]) return null
    const payload = JSON.parse(atob(parts[1]))
    return payload
  } catch {
    return null
  }
}

/**
 * Check if a JWT token is expired.
 * Returns true if expired or if we can't determine expiration.
 */
function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token)
  if (!payload || typeof payload.exp !== 'number') {
    // Can't determine expiration, assume expired to be safe
    return true
  }
  // exp is in seconds, Date.now() is in milliseconds
  // Add 60 second buffer to avoid edge cases
  return payload.exp * 1000 < Date.now() + 60000
}

/**
 * Migrate stale auth tokens from localStorage.
 *
 * The API client reads `auth_token` from localStorage and sends it with every request.
 * If this token is stale/invalid (e.g., from a previous session that's no longer valid),
 * it can cause API requests to fail (500 errors from backend token validation).
 *
 * This migration ensures `auth_token` is only present when we have a valid auth session.
 */
// Pre-logger-init: singleton not yet available
function migrateAuthToken(): void {
  const AUTH_TOKEN_KEY = 'auth_token'
  const AUTH_STORAGE_KEY = 'auth-storage'

  try {
    const rawAuthToken = localStorage.getItem(AUTH_TOKEN_KEY)
    const rawAuthStorage = localStorage.getItem(AUTH_STORAGE_KEY)

    // If there's no auth_token, nothing to migrate
    if (!rawAuthToken) {
      return
    }

    // Check if the current auth_token is expired
    if (isTokenExpired(rawAuthToken)) {
      console.log('[Auth Migration] Clearing expired auth_token (Supabase SDK will refresh session)')
      localStorage.removeItem(AUTH_TOKEN_KEY)
      // Do NOT clear auth-storage or Supabase's sb-*-auth-token keys.
      // The Supabase SDK stores the refresh token (30-day lifespan) in its own
      // localStorage entry and will use it to silently obtain a fresh access token.
      // Deleting those keys forces re-login even when the refresh token is still valid.
      return
    }

    // If there's an auth_token but no auth-storage, the token is orphaned - clear it
    if (!rawAuthStorage) {
      console.log('[Auth Migration] Clearing orphaned auth_token (no auth-storage)')
      localStorage.removeItem(AUTH_TOKEN_KEY)
      return
    }

    // Parse auth-storage to check if we have a valid session
    const authStorage = JSON.parse(rawAuthStorage)
    const storedAccessToken = authStorage?.state?.token?.accessToken

    // If auth-storage doesn't have a valid token, clear auth_token
    if (!storedAccessToken) {
      console.log('[Auth Migration] Clearing auth_token (auth-storage has no valid token)')
      localStorage.removeItem(AUTH_TOKEN_KEY)
      return
    }

    // If auth_token doesn't match the token in auth-storage, sync them
    if (rawAuthToken !== storedAccessToken) {
      console.log('[Auth Migration] Syncing auth_token with auth-storage token')
      localStorage.setItem(AUTH_TOKEN_KEY, storedAccessToken)
    }
  } catch (error) {
    // If anything goes wrong parsing, clear the token to be safe
    console.warn('[Auth Migration] Error during migration, clearing auth_token:', error)
    localStorage.removeItem(AUTH_TOKEN_KEY)
  }
}

// Run auth token migration before anything else
migrateAuthToken()

// Prevent all drag operations (images, links, etc.) for native app feel.
// -webkit-user-drag in CSS covers Chrome/Safari; this covers Firefox.
document.addEventListener('dragstart', (e) => e.preventDefault())

async function checkDebugMode(businessId?: string): Promise<boolean> {
  if (!businessId) return false

  try {
    const supabase = getSupabaseClient()
    const { data, error } = await supabase
      .rpc('get_debug_mode', { p_business_id: businessId })

    if (error || data === null || data === undefined) return false
    return data === true
  } catch {
    return false
  }
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      // Don't retry client errors. This used to test `error.status`, which
      // api-client never sets — createError only sets `.type` and `.statusCode`
      // — so the guard was always false and every 401 was retried 3 times, each
      // attempt triggering its own doomed token refresh.
      retry: shouldRetryQuery,
    },
    mutations: {
      retry: false,
    },
  },
})

// Auth callbacks shared between the local API client and the singleton.
// Web omits onAfterLogout — the router reacts to isAuthenticated.
const authCallbacks = createAuthCallbacks()

// Configure the singleton API client with auth callbacks so all hooks
// that use it get token refresh and unauthorized handling
singletonApiClient.configure(authCallbacks)

// Create a local API client for brand initialization (always hits production)
const apiClient = new ApiClient({
  baseURL: import.meta.env.VITE_FLASK_BASE_URL || 'https://api.thalione.tech',
  ...authCallbacks,
})
// Showcase renders must not flood the prod log DB with per-lead boot noise,
// nor depend on api.thalione.tech reachability. Spec §2.
if (!isShowcase()) {
  createWebLogger()
}
const restaurantService = new RestaurantService({ apiClient })

// Initialize layout system to detect mobile/desktop viewport
initializeLayoutSystem()

// Apply debug mode if active — resolved independently so it isn't lost on brand-init failure
function applyDebugMode(isDebugMode: boolean): void {
  if (isDebugMode) {
    const sandboxUrl = 'https://sandbox-api.thalione.tech'
    singletonApiClient.setBaseURL(sandboxUrl)
    setAppDebugMode(true)
    console.log('[Debug Mode] Enabled — API routing to sandbox:', sandboxUrl)
  }
}

/**
 * Fetch restaurant data and populate the store (locations, brand, etc.).
 * Mirrors useRestaurantData hook but runs imperatively during init.
 */
async function prefetchRestaurantData(businessId: string, userLocation: UserLocation | null): Promise<void> {
  const { setRestaurant, setLocation, setLocations } = useRestaurantStore.getState()
  const restaurant = await restaurantService.getRestaurant(businessId)

  const { getAppDebugMode: getDebug } = await import('./config/debugMode')
  setRestaurant(getDebug() ? { ...restaurant, isDebugMode: true } : restaurant)

  if (restaurant.locations && restaurant.locations.length > 0) {
    setLocations(restaurant.locations)
    setLocation(findClosestLocation(restaurant.locations, userLocation))
  }
}

/**
 * Fetch menu items for the current restaurant and populate the restaurant store.
 * Must be called after prefetchRestaurantData has populated currentLocation in the store.
 */
async function prefetchMenuData(): Promise<void> {
  const { currentLocation, setCategories } = useRestaurantStore.getState()

  const storeId = currentLocation?.id

  if (!storeId) {
    safeGetLogger()?.debug('prefetchMenuData: no store ID, skipping', { domain: 'menu' })
    return
  }
  safeGetLogger()?.debug('prefetchMenuData: fetching menu', { domain: 'menu', storeId })

  const rawResponse = await singletonApiClient.post<RawMenuResponse>('/stores/menu-items', {
    store_ids: [storeId],
    use_optimized: false,
  })
  const response = filterHiddenFromMenu(rawResponse) as unknown as Record<string, RawMenuDataItem[]>

  const categories = transformMenuResponse(response, storeId)

  safeGetLogger()?.debug('prefetchMenuData: categories processed', { domain: 'menu', count: categories.length })
  setCategories(categories, storeId)
}

/**
 * remountKey is the showcase live-customize remount hook (bootShowcase):
 * components resolve brand colors into JS style literals captured at first
 * mount — there is no subscription/re-render path, so a config-only color
 * update repaints CSS custom properties but not already-mounted components.
 * Keying <App/> forces React to unmount/remount the whole tree so everything
 * re-resolves from the updated configs. Default call site (undefined) keeps
 * the normal single mount — only bootShowcase's remount callback bumps it.
 */
function renderApp(root: ReactDOM.Root, remountKey?: number): void {
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App key={remountKey} />
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </React.StrictMode>,
  )
}

// Resolve tenant, then initialize app with loading screen
async function initializeApp() {
  const root = ReactDOM.createRoot(document.getElementById('root')!)

  if (isShowcase()) {
    const token = getShowcaseToken()
    const payload = token ? await fetchTokenPayload(token) : getShowcasePayload()
    await bootShowcase(root, renderApp, payload)
    return
  }

  try {
    // Step 1: Resolve tenant (may redirect and never return)
    const tenant = await resolveTenant()
    setRestaurantId(tenant.businessId)

    // Step 2: Show loading screen for a minimum hold. The logo animation runs
    // 2.5s (75 frames at 30fps) but its last ~0.5s is empty, so gate on 2s.
    const ANIMATION_DURATION_MS = 2000
    const minLoadingTime = new Promise<void>(r => setTimeout(r, ANIMATION_DURATION_MS))

    root.render(
      <React.StrictMode>
        <LoadingScreen />
      </React.StrictMode>
    )

    // Step 3: Run all initialization in parallel with the animation timer
    const initPromise = (async () => {
      // Brand + debug mode + geolocation
      const results = await Promise.allSettled([
        initializeWebBrand(restaurantService, tenant.businessId),
        checkDebugMode(tenant.businessId),
        geolocationService.getLocation(),
      ])

      const debugResult = results[1]
      applyDebugMode(debugResult.status === 'fulfilled' && debugResult.value === true)

      if (results[0].status === 'rejected') {
        safeGetLogger()?.config('init_fallback', { error: String(results[0].reason), reason: 'brand_init_failed' })
      }

      const userLocation = results[2].status === 'fulfilled' ? results[2].value : null

      // Restaurant data + menu items (must run after debug mode is applied)
      try {
        await prefetchRestaurantData(tenant.businessId, userLocation)
        await prefetchMenuData()
      } catch (dataErr) {
        safeGetLogger()?.warn('Prefetch failed during init, will retry in-app', {
          domain: 'init',
          error: dataErr instanceof Error ? dataErr.message : String(dataErr),
        })
      }
    })()

    // Wait for both init AND one full animation loop
    await Promise.all([initPromise, minLoadingTime])

    // Step 4: Render the app
    renderApp(root)
  } catch (error) {
    // Tenant resolution failed (network error, timeout, etc.)
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    if (errorMessage === 'redirect') return // redirect in progress

    root.render(
      <React.StrictMode>
        <LoadingScreen
          error={errorMessage}
          onRetry={() => window.location.reload()}
        />
      </React.StrictMode>
    )
  }
}

initializeApp()
